/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2021 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 *
 * @file ResourceDataContainer.jsx
 * @author Srinivasan Bakthavatchalam
 * @since 1.0.1
 */

import React, { memo, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';

import EmptyMessage from '../components/common/EmptyMessage';
import messages from '../../translations/defaultMessages';
import ResourceDataComponent from '../components/ResourceDataComponent';
import ResourceUtils from '../utils/ResourceUtils';
import usePrevious from '../components/common/usePrevious';
import { BOOK_LEVEL_RESOURCE, PAGE_LEVEL_RESOURCE, USER_ROLE_STUDENT } from '../common/constants';

const ResourceDataContainer = ({
  bulkDownloadStatus,
  currentPageId,
  filteredContentType,
  isCardView,
  isFilterDropDownOpen,
  intl,
  onResourceClick,
  progressCount,
  resourceFlatData,
  resourceLevelTabValue,
  resourceStructuredData,
  searchedKeyword,
  selectedResources,
  setIsBookResource,
  setIsCurrentTabEmpty,
  setIsPageResource,
  setResourceLevelTab,
  setFilteredResourcesData,
  setSelectedResources,
  showDownload,
  userRoleTabValue
}) => {
  const resourceData = useMemo(() => ResourceUtils.getResourceData(
    (resourceLevelTabValue === BOOK_LEVEL_RESOURCE
      && (filteredContentType.length === 0 && searchedKeyword === '')
      && !isCardView) ? resourceStructuredData : resourceFlatData,
    userRoleTabValue,
    resourceLevelTabValue,
    filteredContentType,
    isCardView,
    searchedKeyword),
  [userRoleTabValue,
    resourceLevelTabValue, filteredContentType, isCardView, searchedKeyword, progressCount]);
  const isSearchAndFilterOff = filteredContentType.length === 0 && searchedKeyword === '';
  const { formatMessage } = intl;
  const prevUserRoleTabValue = usePrevious(userRoleTabValue);
  const prevResourceLevelTabValue = usePrevious(resourceLevelTabValue);

  useEffect(() => {
    if (!isSearchAndFilterOff) {
      setFilteredResourcesData(resourceData);
    }
    if (prevUserRoleTabValue !== userRoleTabValue || prevResourceLevelTabValue !== resourceLevelTabValue) {
      ResourceUtils.handleScrollOnTabChange();
    }
    const currentData = resourceData
    && (Array.isArray(resourceData) ? resourceData.length : Object.keys(resourceData).length);

    if (isSearchAndFilterOff && !currentData) {
      setIsCurrentTabEmpty(true);
    } else {
      setIsCurrentTabEmpty(false);
    }
    const hasPageResource = ResourceUtils.checkPageResource(resourceFlatData);
    if (hasPageResource) {
      setIsPageResource(true);
    } else {
      setIsPageResource(false);
    }
    // check for page resource
    const hasBookResource = ResourceUtils.getResourceData(
      (filteredContentType.length === 0 && searchedKeyword === '')
        && !isCardView ? resourceStructuredData : resourceFlatData,
      userRoleTabValue,
      BOOK_LEVEL_RESOURCE,
      filteredContentType,
      isCardView,
      searchedKeyword);
    // check for book resource
    if (hasBookResource && hasBookResource.length > 0) {
      setIsBookResource(true);
    } else {
      setIsBookResource(false);
    }
    if (userRoleTabValue === USER_ROLE_STUDENT) {
      if (hasBookResource.length > 0 && hasPageResource) {
        setResourceLevelTab(resourceLevelTabValue);
      } else if (hasBookResource.length > 0 && !hasPageResource) {
        setResourceLevelTab(BOOK_LEVEL_RESOURCE);
      } else {
        setResourceLevelTab(PAGE_LEVEL_RESOURCE);
      }
    } else if (hasBookResource.length > 0 && hasPageResource) {
      setResourceLevelTab(resourceLevelTabValue);
    } else if (hasBookResource.length > 0 && !hasPageResource) {
      setResourceLevelTab(BOOK_LEVEL_RESOURCE);
    } else {
      setResourceLevelTab(PAGE_LEVEL_RESOURCE);
    }
  }, [userRoleTabValue,
    resourceLevelTabValue, filteredContentType, isCardView, searchedKeyword]);

  return (resourceData
      && (Array.isArray(resourceData) ? resourceData.length : Object.keys(resourceData).length) ? <ResourceDataComponent
        bulkDownloadStatus={bulkDownloadStatus}
        currentPageId={currentPageId}
        isCardView={isCardView}
        isFilterDropDownOpen={isFilterDropDownOpen}
        isSearchAndFilterOff={isSearchAndFilterOff}
        level={1}
        onResourceClick={onResourceClick}
        resourceData={resourceData}
        resourceLevelTabValue={resourceLevelTabValue}
        selectedResources={selectedResources}
        setSelectedResources={setSelectedResources}
        showDownload={showDownload}
        userRoleTabValue={userRoleTabValue}
      /> :
    !isSearchAndFilterOff && <EmptyMessage
      messageHeading={isSearchAndFilterOff ? formatMessage(messages.noResources) : formatMessage(messages.noResults)}
      messageDesciption={
        formatMessage(messages[ResourceUtils.getEmptyScreenDescription(isSearchAndFilterOff,
          resourceLevelTabValue === BOOK_LEVEL_RESOURCE)])}
    />);
};

ResourceDataContainer.propTypes = {
  bulkDownloadStatus: PropTypes.string.isRequired,
  currentPageId: PropTypes.array.isRequired,
  filteredContentType: PropTypes.array.isRequired,
  isCardView: PropTypes.bool.isRequired,
  isFilterDropDownOpen: PropTypes.bool.isRequired,
  intl: intlShape,
  onResourceClick: PropTypes.func.isRequired,
  progressCount: PropTypes.number.isRequired,
  resourceFlatData: PropTypes.array,
  resourceLevelTabValue: PropTypes.string.isRequired,
  resourceStructuredData: PropTypes.array,
  searchedKeyword: PropTypes.string.isRequired,
  selectedResources: PropTypes.array.isRequired,
  setIsBookResource: PropTypes.func,
  setIsCurrentTabEmpty: PropTypes.func,
  setIsPageResource: PropTypes.func,
  setResourceLevelTab: PropTypes.func,
  setFilteredResourcesData: PropTypes.func,
  setSelectedResources: PropTypes.func,
  showDownload: PropTypes.bool.isRequired,
  userRoleTabValue: PropTypes.string.isRequired
};
ResourceDataContainer.defaultProps = {
  intl: {},
  resourceFlatData: [],
  resourceStructuredData: [],
  setIsBookResource: () => {},
  setIsCurrentTabEmpty: () => {},
  setIsPageResource: () => {},
  setResourceLevelTab: () => {},
  setFilteredResourcesData: () => {},
  setSelectedResources: () => {}
};
export default memo(injectIntl(ResourceDataContainer));
